import { BehaviorSubject } from 'rxjs';
import { SqliteProvider, QueryResult } from '../providers';
import { IChangeTracker } from '@app/domain/ports';

export function wrapProviderWithChangeTracker(
  provider: SqliteProvider,
  changeTracker?: IChangeTracker,
): SqliteProvider {
  if (!changeTracker) {
    return provider;
  }

  const wrapped: SqliteProvider = {
    initialize: provider.initialize.bind(provider),
    import: async (data: Uint8Array): Promise<void> => {
      await provider.import(data);
      changeTracker.markDirty();
    },
    export: provider.export.bind(provider),
    getRowsModified: provider.getRowsModified.bind(provider),
    close: provider.close.bind(provider),
    beginTransaction: provider.beginTransaction.bind(provider),
    commit: provider.commit.bind(provider),
    rollback: provider.rollback.bind(provider),

    execute: async (sql: string, params?: unknown[]): Promise<QueryResult[]> => {
      const results = await provider.execute(sql, params);
      const isWriteOperation = /^\s*(INSERT|UPDATE|DELETE|REPLACE)/i.test(sql);
      if (isWriteOperation) {
        const rowsModified = await provider.getRowsModified();
        if (rowsModified > 0) {
          changeTracker.markDirty();
        }
      }

      return results;
    },
  };

  const executeObjects = provider.executeObjects;
  if (executeObjects) {
    wrapped.executeObjects = async <TRow extends Record<string, unknown> = Record<string, unknown>>(
      sql: string,
      params?: unknown[],
    ): Promise<TRow[]> => {
      const rows = await executeObjects(sql, params);
      const isWriteOperation = /^\s*(INSERT|UPDATE|DELETE|REPLACE)/i.test(sql);
      if (isWriteOperation) {
        const rowsModified = await provider.getRowsModified();
        if (rowsModified > 0) {
          changeTracker.markDirty();
        }
      }

      return rows as TRow[];
    };
  }

  return wrapped;
}

export class ChangeTracker implements IChangeTracker {
  private hasChangesSubject = new BehaviorSubject<boolean>(false);
  readonly hasChanges$ = this.hasChangesSubject.asObservable();

  isDirty(): boolean {
    return this.hasChangesSubject.getValue();
  }

  markDirty(): void {
    this.hasChangesSubject.next(true);
  }

  markClean(): void {
    this.hasChangesSubject.next(false);
  }

  destroy(): void {
    this.hasChangesSubject.complete();
  }
}
